import React, { useCallback } from 'react';
import { productShape } from 'shapes';
import classnames from 'classnames';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { string, bool, func, number, shape } from 'prop-types';
import './AddToCartButton.scss';

const messages = defineMessages({
    outOfStock: {
        id: 'listing.productCard.actionBlock.addToCartButton.outOfStockError',
        defaultMessage: 'Out of stock',
        description: 'Error message when product is out of stock',
    },
});

export default function AddToCartButton({
    product,
    className,
    isB2B,
    dataPosition,
    dataSponsored,
    onClick,
    unableRedirectToModels,
}) {
    const intl = useIntl();

    const shouldRedirectToModels =
        product.models_count > 1 && !unableRedirectToModels;

    const handleClick = useCallback(
        evt => {
            if (shouldRedirectToModels) return;

            evt.stopPropagation();
            evt.preventDefault();

            if (onClick) {
                onClick(evt);
            }
        },
        [onClick, shouldRedirectToModels],
    );

    const buttonClasses = classnames(
        'add-to-cart-button__button',
        'button',
        className,
        {
            'add-to-cart-button--b2b': isB2B,
        },
    );

    return (
        <div className="add-to-cart-button">
            <button
                type="button"
                className={buttonClasses}
                data-event="add_to_cart"
                data-url={`/cart/product/${product.objectID}/update`}
                data-quantity={product.minQuantity || 1}
                data-error={intl.formatMessage(messages.outOfStock)}
                data-product-id={product.objectID}
                data-name={product.title}
                data-button="addToBasket"
                data-article-id={product.article_id}
                data-category-id={product['categories.last_id']}
                data-category={product['categories.last']}
                data-has-x-payment={product.has_3x_payment ? 'true' : 'false'}
                data-model-id={product.model_id}
                data-price={product.price}
                data-seller-id={product.seller_id}
                data-seller-name={product.seller_name}
                data-product-sku={product.sku}
                data-position={dataPosition}
                data-sponsored-id={dataSponsored && dataSponsored.id}
                onClick={handleClick}
            >
                <span className="add-to-cart__label">
                    {shouldRedirectToModels ? (
                        <FormattedMessage
                            id="listing.productCard.actionBlock.seeModels"
                            defaultMessage="See the {count} models"
                            description="add to cart label of the product card"
                            values={{
                                count: product.models_count,
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="listing.productCard.actionBlock.addToCartButton"
                            defaultMessage="Add to cart"
                            description="add to cart label of the product card"
                        />
                    )}
                </span>
            </button>
        </div>
    );
}

AddToCartButton.propTypes = {
    product: productShape.isRequired,
    className: string,
    isB2B: bool.isRequired,
    dataPosition: number,
    dataSponsored: shape({
        status: bool,
        id: string,
    }),
    onClick: func,
    unableRedirectToModels: bool,
};

AddToCartButton.defaultProps = {
    className: null,
    dataPosition: null,
    dataSponsored: {
        status: false,
        id: undefined,
    },
    onClick: null,
    unableRedirectToModels: false,
};
